/**
 * /enrollments/:id (tracker item 85): the "these visits are bundled" view.
 * Header with the bundle name, the Auto-renew toggle, property and start date;
 * a progress bar; the vertical timeline of visits; the total; Renew (opens the
 * quote), Cancel enrollment and, for a PENDING_APPROVAL enrollment, the
 * pending banner with Approve / Decline. Editing a visit price uses the
 * renewal price prompt.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createAPIClient, IBundlesApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobDate, formatJobMoney } from '../../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { ApproveEnrollmentPanel } from '../../features/service-bundles/ApproveEnrollmentPanel';
import { EnrollmentTimeline, ProgressBar } from '../../features/service-bundles/EnrollmentTimeline';
import { RenewalQuoteDialog } from '../../features/service-bundles/RenewalQuoteDialog';
import { isRenewalPriceCancelled, useRenewalPriceChoice } from '../../features/service-bundles/RenewalPricePrompt';
import { RENEWALS_PATH, autoRenewNote, enrollmentPath, enrollmentStatusClasses, enrollmentStatusLabel, nextVisitText, progressText } from '../../features/service-bundles/bundleUi';

type PageApi = IBundlesApi & Pick<IUsersApi, 'listAssignableUsers'>;

export function EnrollmentPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo(() => createAPIClient() as PageApi, []);
  const navigate = useNavigate();
  const { withChoice, dialog: priceDialog } = useRenewalPriceChoice();
  const [enrollment, setEnrollment] = useState<types.BundleEnrollment | null>(null);
  const [technicians, setTechnicians] = useState<Record<string, string>>({});
  const [sourceYear, setSourceYear] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editing, setEditing] = useState<{ step: types.EnrollmentStep; value: string } | null>(null);

  const load = useCallback(async () => {
    const fresh = await api.getEnrollment(id);
    setEnrollment(fresh);
    return fresh;
  }, [api, id]);

  useEffect(() => {
    let active = true;
    setEnrollment(null);
    setLoadError(null);
    setApproving(false);
    setNotice(null);
    api.getEnrollment(id)
      .then((fresh) => {
        if (!active) return;
        setEnrollment(fresh);
        if (fresh.renewedFromId) api.getEnrollment(fresh.renewedFromId).then((source) => { if (active) setSourceYear(Number(source.startDate.slice(0, 4))); }).catch(() => undefined);
        else setSourceYear(null);
      })
      .catch((err) => { if (active) setLoadError(getErrorMessage(err, 'Could not load the enrollment')); });
    api.listAssignableUsers().then((users) => { if (active) setTechnicians(Object.fromEntries(users.map((u) => [u.id, u.email]))); }).catch(() => undefined);
    return () => { active = false; };
  }, [api, id]);

  const run = async (work: () => Promise<void>, failure: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try { await work(); } catch (err) { if (!isRenewalPriceCancelled(err)) setError(getErrorMessage(err, failure)); } finally { setBusy(false); }
  };

  if (loadError) {
    return <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6"><Toast kind="error">{loadError}</Toast><p className="text-gray-700">The enrollment could not be loaded.</p><Link to={RENEWALS_PATH} className="text-blue-700 underline">Renewals</Link></main>;
  }
  if (!enrollment) return <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6"><p role="status">Loading enrollment...</p></main>;

  const pending = enrollment.status === 'PENDING_APPROVAL';
  const renewable = enrollment.status === 'ACTIVE' || enrollment.status === 'COMPLETED';
  const note = autoRenewNote(enrollment, sourceYear);
  const next = nextVisitText(enrollment.nextVisitDate);
  const stepTitle = (step: types.EnrollmentStep) => `${step.itemName} (${step.step} of ${enrollment.stepCount})`;

  const toggleAutoRenew = (value: boolean) => run(async () => {
    setEnrollment(await api.updateEnrollmentAutoRenew(enrollment.id, value));
    setNotice(value ? 'Auto-renew is on.' : 'Auto-renew is off.');
  }, 'Could not change auto-renew');

  const savePrice = () => run(async () => {
    if (!editing) return;
    const price = editing.value.trim();
    if (price === '' || !Number.isFinite(Number(price)) || Number(price) < 0) { setError('Enter a price of 0 or more.'); return; }
    setEnrollment(await withChoice((action) => api.updateEnrollmentStepPrice(enrollment.id, editing.step.step, { unitPrice: price, ...(action ? { renewalPriceAction: action } : {}) }), stepTitle(editing.step)));
    setEditing(null);
    setNotice('Visit price saved.');
  }, 'Could not save the visit price');

  const doCancel = () => run(async () => {
    setEnrollment(await api.cancelEnrollment(enrollment.id, cancelReason.trim() || undefined));
    setCancelling(false);
    setNotice('Enrollment cancelled. Visits that had not started were cancelled.');
  }, 'Could not cancel the enrollment');

  const doDecline = () => run(async () => {
    setEnrollment(await api.declineEnrollment(enrollment.id, declineReason.trim() || undefined));
    setDeclining(false);
    setNotice('Declined. Nothing was scheduled.');
  }, 'Could not decline the enrollment');

  return (
    <main className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6">
      <div>
        <Link to={`/customers/${enrollment.customerId}`} className="text-sm text-blue-700 hover:underline">← {enrollment.customer.name}</Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="break-words text-2xl font-bold text-gray-900">{enrollment.bundleName}</h1>
            <p className="mt-1 text-sm text-gray-600">
              <Link to={`/customers/${enrollment.customerId}/properties/${enrollment.propertyId}`} className="text-blue-700 hover:underline">{enrollment.property.address}, {enrollment.property.city}</Link>
              {' · '}Starts {formatJobDate(enrollment.startDate)}
            </p>
          </div>
          <span className={`rounded-full px-3 py-1 text-sm font-semibold ${enrollmentStatusClasses(enrollment.status)}`}>{enrollmentStatusLabel(enrollment.status)}</span>
        </div>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}

      {pending && (
        <section aria-label="Pending approval" className="rounded-lg border border-amber-300 bg-amber-50 p-4 sm:p-5" data-testid="pending-banner">
          <h2 className="font-semibold text-amber-900">Pending approval</h2>
          <p className="mt-1 text-sm text-amber-900">
            {enrollment.renewedFromId ? 'This renewal' : 'This request'} has no jobs yet. Nothing is scheduled or billed until you approve it.
            {' '}First visit {formatJobDate(enrollment.startDate)}, {formatJobMoney(enrollment.total)} before tax.
            {enrollment.renewedFromId && <> <Link to={enrollmentPath(enrollment.renewedFromId)} className="font-semibold underline">See the enrollment it renews</Link>.</>}
          </p>
          {!approving && !declining && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className={primaryButtonClass} onClick={() => setApproving(true)}>Approve</button>
              <button type="button" className={secondaryButtonClass} onClick={() => setDeclining(true)}>Decline</button>
            </div>
          )}
          {declining && (
            <div className="mt-3 max-w-md">
              <label htmlFor="decline-reason" className={labelClass}>Reason (optional)</label>
              <textarea id="decline-reason" className={inputClass} rows={2} value={declineReason} maxLength={500} onChange={(e) => setDeclineReason(e.target.value)} />
              <div className="mt-2 flex gap-2">
                <button type="button" className={primaryButtonClass} disabled={busy} onClick={doDecline}>Decline renewal</button>
                <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setDeclining(false)}>Keep it</button>
              </div>
            </div>
          )}
        </section>
      )}
      {pending && approving && (
        <ApproveEnrollmentPanel
          enrollment={enrollment}
          onCancel={() => setApproving(false)}
          onApproved={(approved) => { setEnrollment(approved); setApproving(false); setNotice('Approved. The visits were created.'); }}
        />
      )}

      {enrollment.status === 'CANCELLED' && (
        <p role="note" className="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-800">
          Cancelled{enrollment.cancelledAt ? ` on ${formatJobDate(enrollment.cancelledAt)}` : ''}{enrollment.cancelReason ? `: ${enrollment.cancelReason}` : ''}.
        </p>
      )}

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-label="Progress">
        {!pending && <ProgressBar progress={enrollment.progress} label={progressText(enrollment.progress)} />}
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <input id="auto-renew" type="checkbox" className="mt-1 h-5 w-5" checked={enrollment.autoRenew} disabled={busy || enrollment.status === 'CANCELLED'} onChange={(e) => toggleAutoRenew(e.target.checked)} />
            <div>
              <label htmlFor="auto-renew" className="font-medium text-gray-900">Auto-renew</label>
              {note && <p className="text-sm text-gray-600" data-testid="auto-renew-note">{note}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">{pending ? 'Quoted total before tax' : 'Total of all visits'}</p>
            <p className="text-xl font-bold text-gray-900" data-testid="enrollment-total">{formatJobMoney(enrollment.total)}</p>
            {!pending && next && <p className="mt-1 text-sm font-medium text-blue-800">{next}</p>}
          </div>
        </div>
      </section>

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-label="Visits">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Visits</h2>
        <EnrollmentTimeline enrollment={enrollment} technicians={technicians} showNext={false} onEditPrice={enrollment.status === 'CANCELLED' ? undefined : (step) => setEditing({ step, value: step.jobUnitPrice ?? step.unitPrice })} />
        {editing && (
          <form className="mt-2 rounded border border-gray-200 bg-gray-50 p-4" onSubmit={(e) => { e.preventDefault(); void savePrice(); }} noValidate>
            <label htmlFor="step-price" className={labelClass}>Price per unit for visit {editing.step.step}, {editing.step.itemName}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input id="step-price" type="number" min="0" step="0.01" className={`${inputClass} max-w-[10rem]`} value={editing.value} disabled={busy} onChange={(e) => setEditing({ ...editing, value: e.target.value })} />
              <button type="submit" className={primaryButtonClass} disabled={busy}>Save price</button>
              <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
            </div>
            <p className="mt-2 text-xs text-gray-500">If the price changes you will be asked whether the renewal price follows.</p>
          </form>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-3">
        {renewable && !enrollment.renewedToId && <button type="button" className={primaryButtonClass} onClick={() => setRenewing(true)}>Renew</button>}
        {enrollment.renewedToId && <Link to={enrollmentPath(enrollment.renewedToId)} className={`${secondaryButtonClass} inline-flex items-center`}>View renewal</Link>}
        {enrollment.status === 'ACTIVE' && !cancelling && <button type="button" className="py-2 text-sm font-semibold text-red-700 hover:underline" onClick={() => setCancelling(true)}>Cancel enrollment</button>}
      </div>
      {cancelling && (
        <section aria-label="Cancel enrollment" className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-900">Cancels every visit that has not started. Started, completed and invoiced visits are left alone.</p>
          <label htmlFor="cancel-reason" className={`${labelClass} mt-3`}>Reason (optional)</label>
          <textarea id="cancel-reason" className={inputClass} rows={2} maxLength={500} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <div className="mt-3 flex gap-2">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={doCancel}>Confirm cancellation</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setCancelling(false)}>Keep enrollment</button>
          </div>
        </section>
      )}

      {renewing && (
        <RenewalQuoteDialog
          enrollment={enrollment}
          onClose={() => setRenewing(false)}
          onCreated={(result) => { setRenewing(false); void load().catch(() => undefined); navigate(enrollmentPath(result.renewal.id)); }}
        />
      )}
      {priceDialog}
    </main>
  );
}
